import React from 'react';
import MetaTags from 'react-meta-tags';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { CATALOG_IMAGE_FALLBACK, isLinkOutOfStock } from '../../Helpers/shop_helpers';

const ProductMetaTags = props => {
  const { product } = props;
  const { brand } = product;

  // Title, Description, Image
  const title = `${brand?.name} | ${product.title}`;
  const ogTitle = title;
  const description = product.description;
  const ogDescription = description;
  const ogImage = product.images?.[0]?.url || product.image;

  const generateOfferFromLink = link => {
    return {
      '@type': 'Offer',
      price: link.url_data.productPrice || '0.00',
      priceCurrency: link.url_data.productCurrency || 'USD',
      availability: isLinkOutOfStock(link) ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
      category: product.category?.name || 'Unknown Category',
      itemCondition: 'https://schema.org/NewCondition',
      sku: link.url_data?.productSku || product.id?.toString(),
      seller: {
        '@type': 'Organization',
        name: link.merchant?.name || 'Unknown Seller'
      }
    };
  };

  const validOffers = product.links?.filter(l => l.url_data.productPrice) ?? [];

  // JSON-LD Schema
  const productSchema = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: title || 'Unknown Product',
    image: ogImage || CATALOG_IMAGE_FALLBACK,
    description: description,
    brand: {
      '@type': 'Brand',
      name: brand?.name || 'Unknown Brand'
    },
    sku: product.id?.toString(),
    itemCondition: 'https://schema.org/NewCondition',
    offers: validOffers.length
      ? [
          {
            '@type': 'AggregateOffer',
            offerCount: product.links.length,
            lowPrice: _.min(_.map(validOffers, 'url_data.productPrice')) ?? '0.00',
            priceCurrency: product.fallbackPriceCurrency || 'USD',
            offers: validOffers.slice(0, 10).map(generateOfferFromLink)
          }
        ]
      : [
          {
            '@type': 'Offer',
            price: product.fallbackPrice || 0,
            priceCurrency: product.fallbackPriceCurrency || 'USD',
            availability: 'https://schema.org/InStock',
            seller: {
              '@type': 'Organization',
              name: brand?.name || 'Unknown Seller'
            }
          }
        ]
  };

  if (product.popularityScore) {
    productSchema.review = {
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: product.popularityScore,
        bestRating: 10
      },
      author: {
        '@type': 'Organization',
        name: 'ShopMy Curators'
      }
    };
  }

  return (
    <MetaTags>
      {title && <title>{title}</title>}
      {ogTitle && <meta property='og:title' content={ogTitle} />}
      {description && <meta property='description' content={description} />}
      {ogDescription && <meta property='og:description' content={ogDescription} />}
      {ogImage && <meta property='og:image' content={ogImage} />}
      {/* id necessary to de-dupe when navigating */}
      <script id='schema' type='application/ld+json'>
        {JSON.stringify(productSchema)}
      </script>
    </MetaTags>
  );
};

ProductMetaTags.propTypes = {
  product: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  shop: PropTypes.object.isRequired
};

export default ProductMetaTags;
